import React, { ReactNode, useMemo } from "react";
import { FileText, Eye, RotateCcw } from "lucide-react";
import {
  DataTable,
  ColumnSelector,
  useTableConfig,
  SimpleTooltip,
  TabSelector,
} from "../../common";
import type { TabItem } from "../../common/TabSelector";
import type { TableColumn } from "../../common/Table";
import { ILeaveRequest } from "../../../types/leave.types";
import Badge from "../../common/Badge";
import { getWorkflowStatusVariant } from "../../../utils/badgeVariants";

export type TabFilter = "all" | "pending" | "approved" | "rejected";

const LEAVE_TABS: TabItem<TabFilter>[] = [
  { k: "all", label: "All" },
  { k: "pending", label: "Pending" },
  { k: "approved", label: "Approved" },
  { k: "rejected", label: "Rejected" },
];

interface LeaveTablePaginationProps {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
}

interface LeaveTableSectionProps {
  requests: ILeaveRequest[];
  loading?: boolean;
  activeTab?: TabFilter;
  onTabChange?: (tab: TabFilter) => void;
  onViewDetails: (request: ILeaveRequest) => void;
  onPullback: (request: ILeaveRequest) => void;
  canPullback: (request: ILeaveRequest) => boolean;
  getStatusLabel: (request: ILeaveRequest) => string;
  getStatusColor: (status: string) => string;
  getStatusIcon: (status: string) => ReactNode;
  formatDate: (date: string) => string;
  pagination?: LeaveTablePaginationProps;
}

const LeaveTableSection: React.FC<LeaveTableSectionProps> = ({
  requests,
  loading = false,
  activeTab = "all",
  onTabChange,
  onViewDetails,
  onPullback,
  canPullback,
  getStatusLabel,
  getStatusIcon,
  formatDate,
  pagination,
}) => {
  const sortedRequests = useMemo(
    () =>
      [...requests].sort(
        (a, b) =>
          new Date(b.appliedDate).getTime() - new Date(a.appliedDate).getTime(),
      ),
    [requests],
  );

  const rawColumns = useMemo<TableColumn<ILeaveRequest>[]>(() => {
    return [
      {
        key: "leaveType",
        title: "Leave Type",
        label: "Leave Type",
        required: true,
        render: (_, request) => (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-[8px] bg-[var(--color-bg-muted)] flex items-center justify-center flex-none">
              <FileText className="w-4 h-4 text-[var(--color-text-tertiary)]" />
            </div>
            <span className="text-[13px] font-bold text-[var(--color-text-primary)]">
              {request.leaveTypeName}
            </span>
          </div>
        ),
      },
      {
        key: "period",
        title: "Period",
        label: "Leave Period",
        required: true,
        render: (_, request) => (
          <span className="text-[13px] text-[var(--color-text-secondary)] whitespace-nowrap">
            {formatDate(request.startDate)} - {formatDate(request.endDate)}
          </span>
        ),
      },
      {
        key: "days",
        title: "Days",
        label: "No. of Days",
        render: (_, request) => (
          <span className="text-[13px] font-medium text-[var(--color-text-primary)]">
            {request.days}{" "}
            {request.isHalfDay
              ? request.halfDayType === "second_half"
                ? "second-half"
                : "first-half"
              : "full"}{" "}
            day{request.days !== 1 ? "s" : ""}
          </span>
        ),
      },
      {
        key: "status",
        title: "Status",
        label: "Status",
        render: (_, request) => (
          <Badge
            variant={getWorkflowStatusVariant(request.status)}
            size="middle"
            icon={getStatusIcon(request.status)}
          >
            {getStatusLabel(request)}
          </Badge>
        ),
      },
      {
        key: "appliedDate",
        title: "Applied Date",
        label: "Applied Date",
        render: (_, request) => (
          <span className="text-[13px] text-[var(--color-text-secondary)] whitespace-nowrap">
            {formatDate(request.appliedDate)}
          </span>
        ),
      },
      {
        key: "reason",
        title: "Reason",
        label: "Reason",
        render: (_, request) => {
          const reasonText = request.reason || "—";
          return (
            <SimpleTooltip label={request.reason || "No reason provided"} side="top">
              <span
                className="text-[13px] text-[var(--color-text-secondary)] max-w-[220px] truncate block cursor-default"
              >
                {reasonText}
              </span>
            </SimpleTooltip>
          );
        },
      },
      {
        key: "actions",
        title: "Actions",
        label: "Actions",
        required: true,
        align: "right",
        render: (_, request) => (
          <div className="flex items-center justify-end gap-2">
            <SimpleTooltip label="View Details" side="top">
              <button
                type="button"
                onClick={() => onViewDetails(request)}
                aria-label="View leave details"
                className="w-8 h-8 rounded-[8px] flex items-center justify-center text-[var(--color-text-tertiary)] hover:text-[var(--color-primary-600)] hover:bg-[var(--color-primary-50)] transition-colors border-none bg-transparent cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--color-primary-600)] focus-visible:outline-none"
              >
                <Eye className="w-4 h-4" />
              </button>
            </SimpleTooltip>

            {canPullback(request) && (
              <button
                type="button"
                onClick={() => onPullback(request)}
                className="h-[30px] px-2.5 border border-[var(--color-warning)]/30 rounded-[8px] text-[12px] font-bold text-[var(--color-warning-dark)] hover:bg-[var(--color-warning-light)] inline-flex items-center gap-1.5 transition-colors cursor-pointer bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--color-warning)] focus-visible:outline-none"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Pull back
              </button>
            )}
          </div>
        ),
      },
    ];
  }, [
    canPullback,
    formatDate,
    getStatusIcon,
    getStatusLabel,
    onPullback,
    onViewDetails,
  ]);

  const { configuredColumns, visibleKeys, toggleVisibility, allColumns } =
    useTableConfig(rawColumns, {
      persistenceKey: "my-leaves-history",
    });

  return (
    <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-[18px] overflow-hidden shadow-[var(--shadow-sm)]">
      {/* Table Header Row */}
      <div className="px-[18px] sm:px-[22px] py-[14px] sm:py-[18px] border-b border-[var(--color-border-primary)] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-base font-bold text-[var(--color-text-primary)]">
          Leave History
        </h2>
        <div className="flex items-center gap-2.5 flex-wrap">
          {onTabChange && (
            <TabSelector
              size="sm"
              tabs={LEAVE_TABS}
              active={activeTab}
              onChange={onTabChange}
            />
          )}
          <ColumnSelector
            columns={allColumns}
            visibleKeys={visibleKeys}
            onToggle={toggleVisibility}
          />
        </div>
      </div>

      {/* Reusable Table2 DataTable */}
      <DataTable<ILeaveRequest>
        columns={configuredColumns}
        rows={sortedRequests}
        rowKey={(req) => req.id}
        loading={loading}
        emptyMessage="No leave requests found"
        responsive="auto"
        pagination={
          pagination
            ? {
                page: pagination.currentPage,
                pageSize: pagination.itemsPerPage,
                total: pagination.totalItems,
                pageSizeOptions: pagination.itemsPerPageOptions,
                onPageChange: pagination.onPageChange,
                onPageSizeChange: pagination.onItemsPerPageChange,
              }
            : undefined
        }
      />
    </div>
  );
};

export default LeaveTableSection;
